import React, { startTransition, useEffect, useRef } from "react"
import { expressionHighlight } from "@/lib/code-mirror/expressions"
import { Decoration, EditorView, StateEffect, StateField, useCodeMirror, Range, EditorSelection } from "@uiw/react-codemirror"
import { evaluateExpression, transformValueToExpression } from "@/lib/code-mirror/expressions/expression-evaluator"

interface CodeMirrorEditorProps {
  focusToggler?: any
  value?: string
  onChange?: (value: string) => void
  error?: string
}

const addDecoration = StateEffect.define<Range<Decoration>[]>()

// Define o campo de estado para as decorações
const decorationField = StateField.define({
  create() {
    return Decoration.none
  },
  update(decorations, tr) {
    decorations = decorations.map(tr.changes)
    for (let e of tr.effects) {
      if (e.is(addDecoration)) {
        decorations = Decoration.set(e.value)
      }
    }
    return decorations
  },
  provide: (f) => EditorView.decorations.from(f),
})

const CodeMirrorInput = (props: CodeMirrorEditorProps) => {
  const editor = useRef(null)

  const validateAndDecorate = (view: EditorView, doc: string) => {
    const decorations: Range<Decoration>[] = []
    const regex = /\{\{([^}]*)\}\}/g
    let match

    while ((match = regex.exec(doc)) !== null) {
      const expression = match[1].trim()
      let isValid = true
      try {
        const result = evaluateExpression(`{{${expression}}}`, {})
        if (result === undefined) {
          isValid = false
        }
      } catch (error) {
        isValid = false
      }

      decorations.push({
        from: match.index,
        to: match.index + match[0].length,
        value: Decoration.mark({
          class: isValid ? "cm-valid-expression" : "cm-invalid-expression",
        }),
      })
    }

    view.dispatch({
      effects: addDecoration.of(decorations),
    })
  }

  const { setContainer, view } = useCodeMirror({
    container: editor.current,
    extensions: [
      expressionHighlight(),
      decorationField,
      EditorView.lineWrapping,
      EditorView.theme({
        "&": {
          backgroundColor: "transparent",
        },
        ".cm-scroller": {
          overflow: "auto",
        },
        ".cm-content": {
          maxHeight: "150px",
        },
        ".cm-gutters": {
          display: "none",
        },
        ".cm-activeLine": {
          backgroundColor: "transparent",
        },
      }),
      EditorView.updateListener.of((update) => {
        startTransition(() => {
          if (update.docChanged) {
            const doc = update.state.doc.toString()
            props.onChange?.(doc)
            if (view) {
              validateAndDecorate(view, doc)
            }
          }
        })
      }),
    ],
    ...props,
    // value: typeof props.value === "string" ? props.value : `{{ ${props.value} }}`,
    value: transformValueToExpression(props.value),
    className: props.error && "mb-2",
  })

  // Adiciona a validação inicial quando o view é criado
  useEffect(() => {
    if (view) {
      const doc = view.state.doc.toString()
      validateAndDecorate(view, doc)
    }
  }, [view])

  useEffect(() => {
    if (props.focusToggler) {
      view?.focus()

      // Move o cursor para o final do documento
      const doc = view?.state.doc
      const lastPos = doc?.length ?? 0
      view?.dispatch({
        selection: EditorSelection.single(lastPos),
      })
    }
  }, [props.focusToggler, view])

  return <div ref={editor} />
}

export default CodeMirrorInput
